import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { approveFollowRequest } from "#/lib/api/client/approveFollowRequest"
import { denyFollowRequest } from "#/lib/api/client/denyFollowRequest"
import { getUserPublicKey } from "#/lib/api/client/getUserPublicKey"
import { lookupUserId } from "#/lib/api/client/lookupUserId"
import { sendFollowRequest } from "#/lib/api/client/sendFollowRequest"
import { useCiphers } from "./use-ciphers"
import { useSession } from "#/lib/auth/session-context"
import {
  getReceivedFollowRequestsQueryKey,
  useGetReceivedFollowRequests,
} from "#/lib/api/react-query/useGetReceivedFollowRequests"
import {
  decryptKeyWithRSAPrivateKey,
  decryptWithSymmetricKey,
  encryptKeyWithRSAPublicKey,
  importRSAPublicKey,
} from "#/lib/encryption/crypto"
import {
  getFollowingQueryKey,
  useGetFollowing,
} from "#/lib/api/react-query/useGetFollowing"
import type { GetFollowing200 } from "#/lib/api/types/GetFollowing"
import type { Profile } from "./use-your-profile"
import type { ProtectedProfile } from "#/lib/api/types/ProtectedProfile"

export interface FollowRequest {
  id: string
  username: string
  followerId: string
  requestedAt: string
}

export interface FollowingUser {
  userId: string
  username: string
  encryptedBio?: string
  decryptedBio?: string
}

export function useFollowRequests() {
  const { session } = useSession()
  const { data: ciphers } = useCiphers()
  return useGetReceivedFollowRequests({
    query: {
      select: response => {
        return response.requests.map(
          r =>
            ({
              id: r.requestId,
              followerId: r.followerId,
              username: r.followerUsername,
              requestedAt: r.createdAt,
            }) satisfies FollowRequest,
        )
      },
      enabled: !!session && !!ciphers,
    },
  })
}

export function useSendFollowRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (username: string) => {
      const cleanUsername = username.trim().toLowerCase()
      const response = await lookupUserId({ username: cleanUsername })
      await sendFollowRequest({ followeeId: response.userId })
      return { username: cleanUsername }
    },
    onSuccess: () => {
      // Optionally refresh follow requests or following list
      queryClient.invalidateQueries({
        queryKey: getReceivedFollowRequestsQueryKey(),
      })
    },
  })
}

export function useHandleFollowRequest() {
  const queryClient = useQueryClient()
  const { data: ciphers } = useCiphers()

  return useMutation({
    mutationFn: async ({
      requestId,
      accept,
      requesterId,
    }: {
      requestId: string
      accept: boolean
      requesterId: string
    }) => {
      if (accept) {
        if (!ciphers?.profileSymmetricKey) {
          throw new Error("Profile symmetric key not available")
        }

        // Get the requester's public key
        const pubKeyRes = await getUserPublicKey(requesterId)

        const publicKey = await importRSAPublicKey(pubKeyRes.publicKey)

        const encryptedProfileKey = await encryptKeyWithRSAPublicKey(
          ciphers.profileSymmetricKey,
          publicKey,
        )

        await approveFollowRequest(requestId, {
          protectedProfileSymmetricKey: encryptedProfileKey,
        })
      } else {
        await denyFollowRequest(requestId)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getReceivedFollowRequestsQueryKey(),
      })
      queryClient.invalidateQueries({ queryKey: getFollowingQueryKey() })
    },
  })
}

export function useFollowingUsers() {
  const { session } = useSession()
  const { data: ciphers } = useCiphers()
  const { data: following } = useGetFollowing({
    query: {
      enabled: !!session && !!ciphers,
    },
  })

  return useQuery({
    queryKey: [
      getFollowingQueryKey()[0],
      "decrypted",
      following,
      ciphers?.rsaPrivateKey,
    ],
    queryFn: async () =>
      decryptFollowing(following?.followedUsers, ciphers?.rsaPrivateKey),
    enabled: !!session && !!ciphers && typeof following !== "undefined",
  })
}

async function decryptFollowing(
  following: GetFollowing200["followedUsers"] | undefined,
  rsaPrivateKey: CryptoKey | undefined,
) {
  if (
    typeof following === "undefined" ||
    typeof rsaPrivateKey === "undefined"
  ) {
    // Won't happen due to the enabled check
    throw new Error("impossible state")
  }
  if (following.length === 0) {
    return []
  }

  // Decrypt bios for each user
  const usersWithDecryptedBios = await Promise.all(
    following.map(async user => {
      let decryptedProfile = { bio: "" }

      // If bio is encrypted (stored as JSON string), decrypt it
      try {
        decryptedProfile = await decryptOtherProfile(
          user.profile,
          user.protectedProfileSymmetricKey,
          rsaPrivateKey,
        )
      } catch (e) {
        console.error(
          "Failed to decrypt bio for user:",
          user.profile.username,
          e,
        )
        decryptedProfile = { bio: "Bio unavailable" }
      }

      return {
        userId: user.profile.userId,
        username: user.profile.username,
        decryptedProfile,
      }
    }),
  )

  return usersWithDecryptedBios
}

export async function decryptOtherProfile(
  protectedProfile: ProtectedProfile,
  userProtectedProfileSymmetricKey: string,
  rsaPrivateKey: CryptoKey,
): Promise<Profile> {
  // First extract their profile key using our RSA key
  let theirProfileKey: CryptoKey
  try {
    theirProfileKey = await decryptKeyWithRSAPrivateKey(
      userProtectedProfileSymmetricKey,
      rsaPrivateKey,
    )
  } catch (error) {
    console.error("Failed to decrypt their profile symmetric key", error)
    return { bio: "Bio unavailable" }
  }

  // Now use their decrypted profile key to decrypt their bio
  try {
    if (protectedProfile.bio) {
      const decrypted = await decryptWithSymmetricKey(
        protectedProfile.bio,
        theirProfileKey,
      )
      return { bio: new TextDecoder().decode(decrypted) }
    }
    return { bio: "Bio unavailable" }
  } catch (error) {
    console.error("Failed to decrypt bio:", error)
    return { bio: "Bio unavailable" }
  }
}
